// Resolve a workout exercise name → a free public exercise GIF / image URL.
// Strategy (no API key required):
//   1. Pattern-match the exercise name to known canonical IDs from the public
//      ExerciseDB (https://github.com/yuhonas/free-exercise-db) — an MIT
//      mirror of 800+ exercise GIFs hosted on jsDelivr CDN.
//   2. Fallback to a YouTube search URL so the user always has SOMETHING
//      clickable to see the movement.
//
// All entries below were spot-checked against the jsDelivr CDN. The CDN is
// public, free, doesn't require a key, and serves GIFs as static assets.

const CDN_BASE =
  "https://cdn.jsdelivr.net/gh/yuhonas/free-exercise-db@main/exercises";

// A compact map of common exercise → ExerciseDB filename (without extension).
// Add more here as we ship more workouts. Names are stored lowercased and
// stripped of punctuation for fuzzy matching.
//
// File names map to the public DB asset path: `${CDN_BASE}/<slug>/images/0.jpg`
// (image #0 is the "start" pose; #1 is "end"). We use #0 as the still.
const EXERCISE_SLUGS = {
  // chest
  "bench press":               "Barbell_Bench_Press_-_Medium_Grip",
  "barbell bench press":       "Barbell_Bench_Press_-_Medium_Grip",
  "incline bench press":       "Barbell_Incline_Bench_Press_-_Medium-Grip",
  "incline db press":          "Dumbbell_Bench_Press",
  "dumbbell bench press":      "Dumbbell_Bench_Press",
  "dumbbell flyes":            "Dumbbell_Flyes",
  "push-up":                   "Pushups",
  "push up":                   "Pushups",
  "pushup":                    "Pushups",
  "pushups":                   "Pushups",
  "decline push-up":           "Decline_Pushup",
  // back / pull
  "pull-up":                   "Pullups",
  "pull up":                   "Pullups",
  "pullup":                    "Pullups",
  "chin-up":                   "Chin-Up",
  "barbell row":               "Bent_Over_Barbell_Row",
  "bent over row":             "Bent_Over_Barbell_Row",
  "dumbbell row":              "Bent_Over_Two-Dumbbell_Row",
  "lat pulldown":              "Wide-Grip_Lat_Pulldown",
  "deadlift":                  "Barbell_Deadlift",
  "romanian deadlift":         "Romanian_Deadlift_with_Dumbbells",
  "face pull":                 "Face_Pull",
  // legs / glutes
  "squat":                     "Barbell_Squat",
  "barbell squat":             "Barbell_Squat",
  "goblet squat":              "Goblet_Squat",
  "front squat":               "Front_Barbell_Squat",
  "bulgarian split squat":     "Bulgarian_Dumbbell_Split_Squat",
  "lunge":                     "Dumbbell_Lunges",
  "walking lunge":             "Dumbbell_Lunges",
  "leg press":                 "Leg_Press",
  "leg curl":                  "Lying_Leg_Curls",
  "leg extension":             "Leg_Extensions",
  "hip thrust":                "Barbell_Hip_Thrust",
  "glute bridge":              "Glute_Bridge",
  "calf raise":                "Standing_Dumbbell_Calf_Raise",
  // shoulders
  "overhead press":            "Standing_Military_Press",
  "ohp":                       "Standing_Military_Press",
  "military press":            "Standing_Military_Press",
  "dumbbell shoulder press":   "Seated_Dumbbell_Press",
  "shoulder press":            "Seated_Dumbbell_Press",
  "lateral raise":             "Side_Lateral_Raise",
  "side lateral raise":        "Side_Lateral_Raise",
  "front raise":               "Front_Dumbbell_Raise",
  "rear delt fly":             "Reverse_Flyes",
  // arms
  "bicep curl":                "Dumbbell_Bicep_Curl",
  "dumbbell curl":             "Dumbbell_Bicep_Curl",
  "barbell curl":              "Barbell_Curl",
  "hammer curl":               "Hammer_Curls",
  "tricep pushdown":           "Triceps_Pushdown",
  "tricep dip":                "Bench_Dips",
  "skull crusher":             "Lying_Triceps_Press",
  // core
  "plank":                     "Plank",
  "side plank":                "Side_Plank",
  "crunch":                    "Crunch_-_Hands_Overhead",
  "russian twist":             "Seated_Russian_Twist_Scissor_Kick",
  "leg raise":                 "Hanging_Leg_Raise",
  "mountain climber":          "Mountain_Climbers",
  "ab wheel rollout":          "Ab_Roller",
  // cardio / conditioning
  "burpee":                    "Burpee",
  "jumping jack":              "Jumping_Jacks",
  "high knees":                "High_Knees_Sprint",
  "jump rope":                 "Jump_Rope",
  "kettlebell swing":          "Kettlebell_Swing",
  "box jump":                  "Box_Jump",
};

function normaliseName(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[()[\]{}*×x]+/g, " ")        // strip parens, multiplication
    .replace(/\d+\s*[xX×]\s*\d+\s*\w*/g, "") // "4x8" / "3x10 reps"
    .replace(/\bsets?\b|\breps?\b/g, "")
    .replace(/[^a-z\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function bestSlug(rawName) {
  const name = normaliseName(rawName);
  if (!name) return null;
  // Direct hit
  if (EXERCISE_SLUGS[name]) return EXERCISE_SLUGS[name];
  // Try removing common qualifiers
  const stripped = name.replace(/^(barbell|dumbbell|kettlebell|cable|machine|seated|standing|lying|incline|decline|reverse|wide grip|close grip)\s+/g, "").trim();
  if (stripped !== name && EXERCISE_SLUGS[stripped]) return EXERCISE_SLUGS[stripped];
  // Try with each known key as a substring of the name (longest wins)
  let best = null;
  let bestLen = 0;
  for (const key of Object.keys(EXERCISE_SLUGS)) {
    if (name.includes(key) && key.length > bestLen) {
      best = EXERCISE_SLUGS[key];
      bestLen = key.length;
    }
  }
  return best;
}

/**
 * Look up a visual asset for an exercise name.
 * @returns {null | { image_url:string, youtube_url:string, source:'exercisedb'|'youtube', exercise_name:string }}
 */
export function exerciseVisual(rawName) {
  const slug = bestSlug(rawName);
  const yt = `https://www.youtube.com/results?search_query=${encodeURIComponent(rawName + " exercise form")}`;
  if (!slug) {
    // No CDN match — return the YouTube search as the primary
    return {
      image_url:    null,
      youtube_url:  yt,
      source:       "youtube",
      exercise_name: rawName,
    };
  }
  return {
    image_url:    `${CDN_BASE}/${slug}/images/0.jpg`,
    image_url_alt:`${CDN_BASE}/${slug}/images/1.jpg`,
    youtube_url:  yt,
    source:       "exercisedb",
    exercise_name: rawName,
  };
}
